import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonGroup, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { DataGridPremium, GridColDef, GridToolbarExport, itIT } from '@mui/x-data-grid-premium';
import { EnhancedAccordion } from 'components/enhanced/accordion/EnhancedAccordion';
import LinkEnhancedButton from 'components/ui/buttons/LinkEnhancedButton';
import TenantPage from 'layouts/TenantPage';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LogsFilterForm from '../components/LogsFilterForm';
import ObjectVersionsPanel from '../components/ObjectVersionsPanel';
import VersionDialog from '../components/VersionDialog';
import { useVersionsColumns } from '../hooks/useVersionsColumns';
import { useVersionsData } from '../hooks/useVersionsData';

export const VersionsPage = () => {
  const translationPrefix = 'pages.versions';
  const { models, versions, rowCount, loading, paginationModel, setPaginationModel, handleFilter } =
    useVersionsData();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);
  const [isFiltersAccordionOpen, setIsFiltersAccordionOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerObj, setDrawerObj] = useState<{ modelName: string; objectId: string } | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const { t } = useTranslation();

  const handleOpenDialog = (id: string) => {
    const version = versions.find((v) => v.id === Number(id));
    setSelectedVersion(version);
    setDialogOpen(true);
  };

  const toggleFiltersAccordion = () => {
    setIsFiltersAccordionOpen((prev) => !prev);
  };

  const handleCombinedFilter = (values: Record<string, any>) => {
    handleFilter(values);
    setIsFiltersAccordionOpen(false);
  };

  const columnsWithDrawer: GridColDef[] = useVersionsColumns(
    handleOpenDialog,
    (modelName, objectId) => {
      setDrawerObj({ modelName, objectId });
      setDrawerOpen(true);
    }
  );

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <ButtonGroup>
          <LinkEnhancedButton label={t(`${translationPrefix}.delete-versions`)} to="deleted" />
          <LinkEnhancedButton label={t(`${translationPrefix}.movement-versions`)} to="movement" />
          <LinkEnhancedButton label={t(`${translationPrefix}.sap_sync_logging`)} to="logging" />
          <LinkEnhancedButton
            label={t(`${translationPrefix}.access-logging`)}
            to="access-logging"
          />
        </ButtonGroup>
      }>
      <EnhancedAccordion title="Filtri">
        <LogsFilterForm models={models} onSubmit={handleCombinedFilter} />
      </EnhancedAccordion>

      <DataGridPremium
        density="compact"
        rows={versions}
        loading={loading}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        sx={{ border: 'none', height: '75vh' }}
        disableRowSelectionOnClick
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          setPaginationModel((prev) => ({
            ...newModel,
            page: prev.pageSize !== newModel.pageSize ? 0 : newModel.page
          }));
        }}
        pageSizeOptions={[10, 20, 50]}
        slots={{ toolbar: GridToolbarExport }}
        columns={columnsWithDrawer}
      />

      {selectedVersion && (
        <VersionDialog
          open={dialogOpen}
          onClose={() => {
            setSelectedVersion(null);
            setDialogOpen(false);
          }}
          versionId={selectedVersion?.id}
        />
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerObj(null);
          setDrawerTitle('');
        }}>
        <Box sx={{ width: 720, maxWidth: '92vw', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="primary">
              <b>{drawerTitle || t('pages.versions.object.view.title')}</b>
            </Typography>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
                setDrawerObj(null);
                setDrawerTitle('');
              }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 2 }} />
          {drawerObj && (
            <ObjectVersionsPanel
              modelName={drawerObj.modelName}
              objectId={drawerObj.objectId}
              onMetaChange={({ title, subtitle }) => setDrawerTitle(`${title} — ${subtitle}`)}
            />
          )}
        </Box>
      </Drawer>
    </TenantPage>
  );
};

export default VersionsPage;
